import React from 'react'
import { SEOData, StructuredData } from '@types/index'
import { generateStructuredData } from '@utils/seo'

interface SEOHeadProps extends SEOData {}

const SEOHead: React.FC<SEOHeadProps> = (props) => {
  const {
    title = import.meta.env.VITE_DEFAULT_TITLE || 'HammerMUD',
    description = import.meta.env.VITE_DEFAULT_DESCRIPTION || 'Modern MUD Gaming Experience',
    keywords = [],
    image,
    url = import.meta.env.VITE_SITE_URL || 'https://hammermud.com',
    type = 'website',
    publishedTime,
    modifiedTime,
    author,
    section,
    tags = []
  } = props

  const structuredData = generateStructuredData(props)

  React.useEffect(() => {
    // Update document title
    document.title = title
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`
      let meta = document.querySelector(selector) as HTMLMetaElement
      
      if (!meta) {
        meta = document.createElement('meta')
        if (property) {
          meta.setAttribute('property', name)
        } else {
          meta.setAttribute('name', name)
        }
        document.head.appendChild(meta)
      }
      
      meta.setAttribute('content', content)
    }

    // Basic meta tags
    updateMetaTag('description', description)
    
    if (keywords.length > 0) {
      updateMetaTag('keywords', keywords.join(', '))
    }

    if (author) {
      updateMetaTag('author', author)
    }

    // Open Graph tags
    updateMetaTag('og:type', type, true)
    updateMetaTag('og:title', title, true)
    updateMetaTag('og:description', description, true)
    updateMetaTag('og:url', url, true)
    updateMetaTag('og:site_name', import.meta.env.VITE_SITE_NAME || 'HammerMUD', true)

    if (image) {
      updateMetaTag('og:image', image, true)
      updateMetaTag('og:image:alt', title, true)
    }

    if (publishedTime) {
      updateMetaTag('article:published_time', publishedTime, true)
    }

    if (modifiedTime) {
      updateMetaTag('article:modified_time', modifiedTime, true)
    }

    if (author) {
      updateMetaTag('article:author', author, true)
    }

    if (section) {
      updateMetaTag('article:section', section, true)
    }

    // Remove existing article:tag meta tags
    document.querySelectorAll('meta[property="article:tag"]').forEach(tag => tag.remove())
    
    // Add new article:tag meta tags
    tags.forEach(tag => {
      updateMetaTag('article:tag', tag, true)
    })

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:title', title)
    updateMetaTag('twitter:description', description)

    if (image) {
      updateMetaTag('twitter:image', image)
    }

    if (import.meta.env.VITE_TWITTER_HANDLE) {
      updateMetaTag('twitter:site', import.meta.env.VITE_TWITTER_HANDLE)
    }

    // Additional meta tags
    updateMetaTag('robots', 'index, follow')

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', url)

    // Update structured data
    const existingStructuredData = document.querySelector('script[type="application/ld+json"]')
    if (existingStructuredData) {
      existingStructuredData.remove()
    }

    if (structuredData.length > 0) {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(structuredData.length === 1 ? structuredData[0] : structuredData)
      document.head.appendChild(script)
    }
  }, [
    title, 
    description, 
    keywords, 
    image, 
    url, 
    type, 
    publishedTime, 
    modifiedTime, 
    author, 
    section, 
    tags,
    structuredData
  ])

  return null // This component doesn't render anything visible
}

export default SEOHead